/**
 * StudiesMapSection — "WHERE STUDIES RUN": an abstract, animated dotted world map
 * of every place a StudySync study runs, rebuilt in REAL RetroUI + Tailwind (ZERO
 * @mui/* in this tree).
 *
 * THE MAP is a custom inline <svg> (no map libraries):
 *   - DOTTED CONTINENTS — a STATIC layer of ~3895 faint Cardinal dots
 *     (`WORLD_DOTS` from ./worldDots.ts, pre-projected equirectangular into a
 *     1000×500 viewBox). Never animated per-dot (far too many); the whole group
 *     fades in ONCE on scroll-in.
 *   - MARKERS — one PULSING pin per institution, placed via the SAME projection
 *     the dots use: x=(lng+180)/360*MAP_W, y=(90-lat)/180*MAP_H. A solid brand dot
 *     with an expanding+fading "ping" ring (framer-motion).
 *   - CARDS — a floating white brutalist card per pin (2px black border, hard
 *     offset shadow, square corners) with the institution name, "N studies", and
 *     a compact list of that institution's STUDY NAMES — each linking to its blog
 *     page (/stories/<slug>/) ONLY when that story is actually published (its slug
 *     is present in the live stories manifest); otherwise the name renders as plain,
 *     non-clickable text so we never link to an article that does not exist. The
 *     card is pulled INWARD off its
 *     pin toward the map's horizontal center, joined back to its precise marker by
 *     a clearly visible dashed callout line spanning the gap.
 *
 * THE MAP IS OPEN/AIRY (no frame): the map sits directly on the section's light
 * background — there is no bordered "card" box around it. Its container keeps the
 * sizing role only (relative · aspect-[1000/500] · max-w · mx-auto) and is
 * `overflow-visible` so a hovered card can extend past the map bounds un-clipped.
 *
 * HOVER-REVEAL (desktop, md+): by default ONLY the markers (pulsing dots) show.
 * A pin's card appears when you HOVER or keyboard-FOCUS that marker, and only ONE
 * card shows at a time (the active pin's). Each marker carries a comfortable
 * transparent focusable hit-area (a <button>) since the dots themselves are tiny.
 * Marker + its card live in ONE absolutely-positioned hover GROUP that owns the
 * onMouseEnter/Leave + onFocus/Blur, so the cursor can travel marker→card without
 * dismissing the card before its study links can be clicked. A visible dashed
 * callout line (drawn in the SVG) spans the gap from the marker to the inward-
 * pulled card, only while that pin is active. Because just one card shows at a
 * time, the old Stanford/UC-Irvine California overlap is moot.
 *
 * AGGREGATION — pins are derived IN CODE from MAP_CONFIG (../mapConfig.ts): group
 * by `label`, count entries, UNION their sources, take lat/lng from the first
 * entry. Yields 4 pins: Stanford University (6 studies), UC Irvine (2), Indonesia
 * field site (1), University of Calgary (1).
 *
 * MOBILE (no hover) — the hover-reveal overlay is desktop-only. On small screens
 * the absolute layout is dropped entirely and all the cards render as a plain
 * stacked list UNDER the map so touch users still get every study.
 *
 * REDUCED MOTION (required) — everything is gated on useReducedMotion(): the dots,
 * markers, connectors and the static list still render their FINAL visible state
 * (dots shown, markers static with no pulse, connector drawn while active). The
 * hover card still reveals on hover/focus, but with NO transform/fade — it just
 * toggles visibility instantly. (Note: by design the no-hover resting state now
 * shows only markers; that is expected.)
 *
 * Tailwind Preflight is OFF → every border is declared `border-solid`. Brutalist:
 * zero border-radius EXCEPT `rounded-full` on the map dots/markers. ZERO MUI.
 */
import * as React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MAP_CONFIG } from '../mapConfig';
import type { DataSource, MapStudy } from '../mapConfig';
import { WORLD_DOTS, MAP_W, MAP_H } from './worldDots';
import { useStoriesManifest } from '../../../hooks/useStoriesManifest';

/* ────────────────────────────────────────────────────────────────────────────
 * Aggregation: MAP_CONFIG → one pin per institution
 * ──────────────────────────────────────────────────────────────────────────── */

/** One study belonging to an institution pin — its title + optional deep-link. */
interface PinStudy {
  /** Human-readable study title (may repeat if two config entries share a name). */
  name: string;
  /** Published-story slug for deep-linking, where one exists. */
  storySlug?: string;
}

interface InstitutionPin {
  /** Institution label (group key). */
  label: string;
  /** How many MAP_CONFIG studies belong to this institution. */
  studyCount: number;
  /** The institution's studies, in MAP_CONFIG order (may include duplicate names). */
  studies: PinStudy[];
  /** Union of every grouped study's data sources, in a stable display order. */
  sources: DataSource[];
  /** lat/lng from the group's first entry. */
  lat: number;
  lng: number;
}

/** Stable display order for the source badges, regardless of config ordering. */
const SOURCE_ORDER: DataSource[] = ['healthkit', 'fitbit', 'coremotion', 'ehr'];

/**
 * Group MAP_CONFIG by `label` → one pin each: studyCount = group size, studies =
 * the group's { name, storySlug } in config order (duplicate names allowed),
 * sources = UNION of the group's sources (deduped, ordered by SOURCE_ORDER),
 * lat/lng from the FIRST entry in the group. Insertion order of first-seen labels
 * is preserved so the pin order is deterministic.
 */
function aggregatePins(studies: readonly MapStudy[]): InstitutionPin[] {
  const byLabel = new Map<string, InstitutionPin & { _seen: Set<DataSource> }>();

  for (const study of studies) {
    let pin = byLabel.get(study.label);
    if (!pin) {
      pin = {
        label: study.label,
        studyCount: 0,
        studies: [],
        sources: [],
        lat: study.lat,
        lng: study.lng,
        _seen: new Set<DataSource>(),
      };
      byLabel.set(study.label, pin);
    }
    pin.studyCount += 1;
    pin.studies.push({ name: study.name, storySlug: study.storySlug });
    for (const s of study.sources) pin._seen.add(s);
  }

  return Array.from(byLabel.values()).map((pin) => ({
    label: pin.label,
    studyCount: pin.studyCount,
    studies: pin.studies,
    lat: pin.lat,
    lng: pin.lng,
    sources: SOURCE_ORDER.filter((s) => pin._seen.has(s)),
  }));
}

/** Equirectangular projection — IDENTICAL to how WORLD_DOTS were projected. */
function project(lat: number, lng: number): { x: number; y: number } {
  return {
    x: ((lng + 180) / 360) * MAP_W,
    y: ((90 - lat) / 180) * MAP_H,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * Per-pin presentation (brand color + which side its card anchors to)
 * ──────────────────────────────────────────────────────────────────────────── */

type CardSide = 'left' | 'right';

interface PinStyle {
  /** Hex for the solid marker dot + ping ring. */
  color: string;
  /**
   * Which side the hover popover anchors to, relative to ITS OWN marker, so the
   * card never covers the dot it belongs to. California pins (near the left edge)
   * open to the RIGHT; the Indonesia pin (near the right edge) opens to the LEFT.
   */
  side: CardSide;
  /**
   * The card's NEAR edge, in viewBox X units (0–1000), pulled INWARD toward the
   * map's horizontal center so the card sits well off its pin with a clear gap.
   * This single value anchors BOTH the HTML card (converted to a container %) AND
   * the SVG connector's far endpoint, so the dashed callout line meets the card
   * edge exactly and the two scale together. For a `right`-opening card this is the
   * card's LEFT edge; for a `left`-opening card it's the card's RIGHT edge.
   *
   * Markers sit at x≈161 (Stanford) / x≈173 (UC Irvine) / x≈797 (Indonesia) /
   * x≈183 (Calgary). The two California cards open RIGHT into the central-left
   * zone (x≈370 / x≈395 — a touch apart, though only one shows at a time); Calgary
   * opens RIGHT to x≈420; Indonesia opens LEFT toward center (x≈610). Each leaves a
   * deliberate gap the connector spans.
   */
  cardAnchorX: number;
}

/**
 * Marker color + popover side + inward card anchor, keyed by institution label.
 * Stanford → Cardinal, UC Irvine → Teal, Indonesia → green, Calgary → yellow
 * (brand tokens). Only
 * one card shows at a time (hover-reveal), so the old California stacking is gone;
 * each card is pulled toward the map's horizontal center, away from its pin: the
 * California pins (near the left edge) open RIGHT into a central-left zone, and
 * Indonesia (near the right edge) opens LEFT toward center. A fallback keeps any
 * not-explicitly-mapped institution renderable.
 */
const PIN_STYLES: Record<string, PinStyle> = {
  // Cardinal — marker x≈161; card pulled right to x≈370 (37%).
  'Stanford University': { color: '#8C1515', side: 'right', cardAnchorX: 370 },
  // Teal — marker x≈173; card pulled right to x≈395 (39.5%), just off Stanford's.
  'University of California, Irvine': {
    color: '#00505C',
    side: 'right',
    cardAnchorX: 395,
  },
  // green — marker x≈797; card pulled left to x≈610 (61%).
  'Indonesia field site': { color: '#1FA85C', side: 'left', cardAnchorX: 610 },
  // yellow — marker x≈183 (Calgary, AB), just north of the California pins; card
  // pulled right to x≈420 (42%), clear of the California cards at 370/395 (only
  // one card shows at a time).
  'University of Calgary': { color: '#F5C518', side: 'right', cardAnchorX: 420 },
};
const FALLBACK_STYLE: PinStyle = {
  color: '#8C1515',
  side: 'right',
  cardAnchorX: 370,
};

function styleFor(label: string): PinStyle {
  return PIN_STYLES[label] ?? FALLBACK_STYLE;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Derived render model (computed once)
 * ──────────────────────────────────────────────────────────────────────────── */

interface RenderPin extends InstitutionPin {
  /** Projected marker position in the 1000×500 viewBox. */
  x: number;
  y: number;
  style: PinStyle;
}

const PINS: RenderPin[] = aggregatePins(MAP_CONFIG).map((pin) => {
  const { x, y } = project(pin.lat, pin.lng);
  return { ...pin, x, y, style: styleFor(pin.label) };
});

/* ────────────────────────────────────────────────────────────────────────────
 * Card sub-views — the hover popover body, reused by the desktop overlay AND the
 * mobile stacked list.
 *
 * On desktop the body lives inside a popover that is positioned relative to its
 * OWN marker (see PinHotspot below): it shifts to the marker's `side` and is
 * vertically centered on the dot, so it never covers the marker it describes.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The institution's studies as a compact stacked list. A study links to its blog
 * page (`/stories/${slug}/`) — a same-origin internal link resolved through the
 * dev proxy — ONLY when its `storySlug` is present in `publishedSlugs` (the live
 * stories manifest, which omits drafts/unpublished stories). A study with no slug,
 * or whose slug has no published article yet, renders as plain, non-clickable text
 * so the map never links to an article that does not exist. Links read as links in
 * the brutalist system: sans, the pin's brand color, underline on hover, with a
 * small "↗" affordance. Names that repeat (two config entries sharing a name) are
 * allowed and keyed by index.
 *
 * The card is a BOUNDED width, so long titles (e.g. "Particulate Matter & Cystic
 * Fibrosis") must not overflow: each name is `line-clamp-2` (wraps to at most two
 * lines, then ellipsizes). `min-w-0` on the list items lets the clamp actually
 * shrink — flex/block children otherwise refuse to go below their content width.
 * The "↗" rides inline after the (clamped) title so it never wraps onto its own
 * line awkwardly.
 */
const StudyLinks: React.FC<{
  studies: PinStudy[];
  color: string;
  publishedSlugs: Set<string>;
}> = ({ studies, color, publishedSlugs }) => (
  <ul className="mt-2 flex flex-col gap-1.5 pl-1 md:pl-0">
    {studies.map((study, i) =>
      study.storySlug && publishedSlugs.has(study.storySlug) ? (
        <li key={i} className="min-w-0 leading-snug">
          <a
            href={`/stories/${study.storySlug}/`}
            className="line-clamp-2 font-sans text-sm font-medium leading-snug underline-offset-2 hover:underline"
            style={{ color, textDecorationThickness: '2px' }}
          >
            {study.name}
            <span aria-hidden className="ml-0.5 text-[11px]">
              ↗
            </span>
          </a>
        </li>
      ) : (
        <li
          key={i}
          className="line-clamp-2 min-w-0 font-sans text-sm font-medium leading-snug text-foreground"
        >
          {study.name}
        </li>
      ),
    )}
  </ul>
);

/**
 * The card body shared by the desktop floating layout AND the mobile list.
 *
 * The institution name (e.g. the long "University of California, Irvine") is a
 * flex child beside the color dot, so it gets `min-w-0` + `line-clamp-2` to wrap
 * to at most two lines and then ellipsize rather than overflow the bounded card.
 */
const PinCardBody: React.FC<{
  pin: RenderPin;
  publishedSlugs: Set<string>;
}> = ({ pin, publishedSlugs }) => (
  <>
    <div className="flex items-start gap-2">
      <span
        aria-hidden
        className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full border border-solid border-black"
        style={{ backgroundColor: pin.style.color }}
      />
      <h3 className="line-clamp-2 min-w-0 font-head text-base uppercase leading-tight tracking-tight text-foreground">
        {pin.label}
      </h3>
    </div>
    <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
      {pin.studyCount} {pin.studyCount === 1 ? 'study' : 'studies'}
    </p>
    <StudyLinks
      studies={pin.studies}
      color={pin.style.color}
      publishedSlugs={publishedSlugs}
    />
  </>
);

/* ────────────────────────────────────────────────────────────────────────────
 * Section
 * ──────────────────────────────────────────────────────────────────────────── */

const StudiesMapSection: React.FC = () => {
  const reduceMotion = useReducedMotion();

  /* Published-story slugs from the live manifest (drafts / unpublished stories are
   * omitted; the hook swallows every error and returns [] → an empty set). A study
   * name in a pin card is a clickable /stories/<slug>/ link ONLY when its slug is in
   * this set, so the map never links to an article that doesn't exist yet — while
   * the manifest is loading or unavailable, every name is plain text. */
  const { stories: publishedStories } = useStoriesManifest();
  const publishedSlugs = React.useMemo(
    () => new Set(publishedStories.map((s) => s.slug)),
    [publishedStories],
  );

  /* Which pin's card is currently revealed on desktop (by PINS index), or null
   * when nothing is hovered/focused. Only ONE card shows at a time. Hovering a
   * marker OR moving onto its card keeps the same index set; leaving both (or
   * blurring) clears it. The resting state is null → markers only. */
  const [activePin, setActivePin] = React.useState<number | null>(null);

  /* Hover-intent grace timer. The card is pulled INWARD off its marker with a
   * visible gap, so the cursor must cross dead space marker→card to click a
   * study link. Closing the card INSTANTLY on mouse-leave kills it mid-traverse.
   * Instead, leaving an interactive surface starts a short close timer; the next
   * surface the cursor enters (the bridge, then the card) cancels it via
   * openPin() before it fires. Only one card shows at a time, so a single shared
   * timer ref suffices. */
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Open pin `i` and CANCEL any pending close — called on enter/focus of the
   * marker hit-area, the hover bridge, AND the card. Cancelling here is what
   * keeps the card alive as the cursor hands off across the gap. */
  const openPin = React.useCallback((i: number) => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setActivePin(i);
  }, []);

  /* Start (or restart) the grace-delay close. ~250ms gives the cursor time to
   * reach the bridge/card, whose openPin() cancels this before it runs. If the
   * cursor truly left (to nowhere), the timer elapses and the card closes. */
  const CLOSE_DELAY_MS = 250;
  const scheduleClose = React.useCallback(() => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setActivePin(null);
    }, CLOSE_DELAY_MS);
  }, []);

  /* Never leave a stray timer to fire setState after unmount. */
  React.useEffect(
    () => () => {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    },
    [],
  );

  /* Motion presets. Each is empty (→ no animation, final state) under reduced
   * motion. whileInView is one-shot (viewport.once) per the section contract. */
  const dotsAnim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0 },
        whileInView: { opacity: 1 },
        viewport: { once: true, amount: 0.3 },
        transition: { duration: 0.7, ease: 'easeOut' as const },
      };

  const headerAnim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.3 },
        transition: { duration: 0.5, ease: 'easeOut' as const },
      };

  // A staggered reveal for the MOBILE stacked cards (delay added per card below).
  const cardAnim = (delay: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 14 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.3 },
          transition: { duration: 0.45, ease: 'easeOut' as const, delay },
        };

  /**
   * A single SVG marker: a solid brand dot + a pulsing "ping" ring. The ping is a
   * separate <circle> that expands and fades on a loop (framer-motion). Under
   * reduced motion the ping is omitted entirely and the dot is static.
   */
  const Marker: React.FC<{ pin: RenderPin; pingDelay: number }> = ({
    pin,
    pingDelay,
  }) => (
    <g>
      {!reduceMotion && (
        <motion.circle
          cx={pin.x}
          cy={pin.y}
          r={5}
          fill="none"
          stroke={pin.style.color}
          strokeWidth={1.5}
          initial={{ scale: 1, opacity: 0.7 }}
          animate={{ scale: [1, 3.4], opacity: [0.7, 0] }}
          transition={{
            duration: 2.4,
            ease: 'easeOut',
            repeat: Infinity,
            delay: pingDelay,
          }}
          // Expand from the dot's own center, not the SVG origin.
          style={{ transformOrigin: `${pin.x}px ${pin.y}px` }}
        />
      )}
      {/* Solid marker dot — a hair of black ring for brutalist contrast. */}
      <circle
        cx={pin.x}
        cy={pin.y}
        r={5}
        fill={pin.style.color}
        stroke="#1A1A1A"
        strokeWidth={1.2}
      />
    </g>
  );

  /**
   * The CALLOUT CONNECTOR joining a marker to its (inward-pulled) hover card. It is
   * drawn in the SVG — so it scales with the dots — ONLY while that pin is active:
   * a dashed line running horizontally from the marker dot across the gap to the
   * card's NEAR edge (`style.cardAnchorX`, in viewBox units). Because the card is
   * vertically centered on the marker, a flat line at the marker's y meets the
   * card's near edge at its vertical midpoint, reading as a deliberate callout
   * rather than a tiny stub. With motion it "draws" via pathLength on reveal; under
   * reduced motion it appears instantly, fully drawn. The far endpoint comes from
   * the SAME `cardAnchorX` the HTML card uses, so line and card edge stay aligned.
   */
  const Connector: React.FC<{ to: RenderPin }> = ({ to }) => {
    const x2 = to.style.cardAnchorX; // card's near edge, in viewBox units
    const drawAnim = reduceMotion
      ? {}
      : {
          initial: { pathLength: 0, opacity: 0 },
          animate: { pathLength: 1, opacity: 0.9 },
          exit: { pathLength: 0, opacity: 0 },
          transition: { duration: 0.3, ease: 'easeOut' as const },
        };
    return (
      <motion.line
        x1={to.x}
        y1={to.y}
        x2={x2}
        y2={to.y}
        stroke={to.style.color}
        strokeWidth={2}
        strokeDasharray="5 4"
        strokeLinecap="round"
        opacity={reduceMotion ? 0.9 : undefined}
        // Draw outward FROM the marker.
        style={{ transformOrigin: `${to.x}px ${to.y}px` }}
        {...drawAnim}
      />
    );
  };

  /**
   * One DESKTOP hover GROUP per pin. The group spans the WHOLE overlay
   * (`absolute inset-0`, `pointer-events-none`) so its children can be placed by
   * CONTAINER-relative percentages — the card is pulled far off its pin toward the
   * map center, which a zero-size wrapper at the marker could not express in % (a
   * child's % resolve against a zero-size box). The group is pointer-transparent;
   * only the hit-area + card inside it are interactive, so the pin groups stack
   * harmlessly and the rest of the map stays click-through. It still owns the
   * onMouseEnter/Leave + onFocus/Blur for BOTH children (events bubble up from
   * them), so the cursor can travel dot→card across the gap to click a study link
   * without the card dismissing first — the keep-alive hover group is intact.
   *
   * It contains:
   *  - a transparent, comfortably-sized focusable <button> hit-area centered on
   *    the dot (the dot is only ~5px; the hit-area gives a generous hover/focus
   *    target and makes the pin keyboard-reachable), positioned at the marker's
   *    container %, and
   *  - the card popover, rendered only while this pin is `active`, anchored INWARD
   *    at `style.cardAnchorX` (the same viewBox X the SVG connector targets,
   *    converted to a container %) and vertically centered on the dot. A clearly
   *    visible dashed connector (drawn in the SVG) spans the gap between them.
   *
   * Animation is gated on reduced motion: with motion the card fades+scales in via
   * AnimatePresence; under reduced motion it just appears/disappears instantly.
   */
  const HIT_PX = 44; // transparent hover/focus target around the tiny dot
  const PinHotspot: React.FC<{ pin: RenderPin; index: number }> = ({
    pin,
    index,
  }) => {
    const isActive = activePin === index;
    // Shared hover-intent handlers (defined at section scope). openPin cancels
    // the pending close so the cursor can cross marker -> bridge -> card without
    // the card vanishing; scheduleClose starts the ~250ms grace timer on
    // leave/blur. The next surface entered cancels it before it fires.
    const open = () => openPin(index);
    const close = scheduleClose;

    // Marker position as container percentages (linear, no letterboxing) — the
    // hit-area sits here, centered on the dot via a -50%/-50% translate.
    const markerLeftPct = (pin.x / MAP_W) * 100;
    const markerTopPct = (pin.y / MAP_H) * 100;

    // The card's NEAR edge as a container % — pulled inward toward center, off the
    // pin. A `right`-opening card anchors its LEFT edge here (`left: anchorPct%`);
    // a `left`-opening card anchors its RIGHT edge here (`right: (100−anchorPct)%`).
    // The card is vertically centered on the marker (`top: markerTopPct%` + a
    // `-50%` Y translate), so the horizontal connector at the marker's y meets the
    // card's near edge at its midpoint.
    const anchorPct = (pin.style.cardAnchorX / MAP_W) * 100;
    const openLeft = pin.style.side === 'left';
    const sidePosition: React.CSSProperties = openLeft
      ? { right: `${100 - anchorPct}%`, top: `${markerTopPct}%` }
      : { left: `${anchorPct}%`, top: `${markerTopPct}%` };

    // HOVER BRIDGE geometry (active pin only). A transparent strip that fills the
    // dead gap between the marker and the card's near edge, riding the connector
    // line so even a slow/diagonal cursor path keeps the card alive. Horizontal
    // extent is side-agnostic: from the smaller of {marker, card-edge} x to the
    // larger, both in container %. A small px overhang on each end overlaps the
    // marker hit-area and the card edge so there is no sub-pixel seam. It is
    // vertically centered on the marker y with a fixed height covering the
    // cursor's natural arc; it sits BEHIND the card (no z-index → below the card's
    // z-10) so the card and its links stay on top and fully clickable. It only
    // spans this pin's own marker→card row, so it never blocks other pins.
    const bridgeLeftPct = Math.min(markerLeftPct, anchorPct);
    const bridgeWidthPct = Math.abs(anchorPct - markerLeftPct);
    const BRIDGE_OVERHANG_PX = HIT_PX / 2; // reach into the marker hit-area + card
    const BRIDGE_HEIGHT_PX = 56; // tall enough to catch a diagonal marker→card path
    // content-box: `width` is the % gap; equal L/R padding extends the box past
    // both ends; the negative left margin shifts it so the left padding overlaps
    // the marker hit-area and the right padding overlaps the card edge → no seam.
    const bridgeStyle: React.CSSProperties = {
      boxSizing: 'content-box',
      left: `${bridgeLeftPct}%`,
      top: `${markerTopPct}%`,
      width: `${bridgeWidthPct}%`,
      height: BRIDGE_HEIGHT_PX,
      paddingLeft: BRIDGE_OVERHANG_PX,
      paddingRight: BRIDGE_OVERHANG_PX,
      marginLeft: -BRIDGE_OVERHANG_PX,
      transform: 'translateY(-50%)', // center the strip on the marker y
    };

    // Card reveal motion (gated). Scale origin hugs the marker side it grows from;
    // `y: '-50%'` keeps it vertically centered through the scale animation.
    const cardMotion = reduceMotion
      ? {
          // No transform animation — center with a static CSS translate.
          style: {
            ...sidePosition,
            transform: 'translateY(-50%)',
          } as React.CSSProperties,
        }
      : {
          initial: { opacity: 0, scale: 0.92, y: '-50%' },
          animate: { opacity: 1, scale: 1, y: '-50%' },
          exit: { opacity: 0, scale: 0.92, y: '-50%' },
          transition: { duration: 0.18, ease: 'easeOut' as const },
          style: {
            ...sidePosition,
            transformOrigin: openLeft ? 'right center' : 'left center',
          } as React.CSSProperties,
        };

    return (
      <div
        className="pointer-events-none absolute inset-0"
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
      >
        {/* Transparent focusable hit-area, centered on the dot (container %).
            Carries its own hover-intent handlers: leaving it starts the grace
            timer, which the bridge/card cancels via openPin before it fires. */}
        <button
          type="button"
          aria-label={`${pin.label} — ${pin.studyCount} ${
            pin.studyCount === 1 ? 'study' : 'studies'
          }`}
          className="pointer-events-auto absolute block -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full border-0 bg-transparent p-0"
          style={{
            width: HIT_PX,
            height: HIT_PX,
            left: `${markerLeftPct}%`,
            top: `${markerTopPct}%`,
          }}
          onMouseEnter={open}
          onMouseLeave={close}
          onFocus={open}
          onBlur={close}
        />

        {/* Invisible HOVER BRIDGE (active pin only) — fills the gap between the
            marker and the card so the cursor never crosses a dead zone. Same
            hover-intent handlers; sits behind the card (no z-index) so it never
            covers the card or its study links. Transparent, no visual. */}
        {isActive && (
          <div
            aria-hidden
            className="pointer-events-auto absolute bg-transparent"
            style={bridgeStyle}
            onMouseEnter={open}
            onMouseLeave={close}
          />
        )}

        {/* The card popover — only while this pin is active. Its own handlers
            (incl. onFocus/onBlur, which catch focus bubbling from the study
            links) keep it open while the cursor or keyboard focus is inside. */}
        <AnimatePresence>
          {isActive && (
            <motion.div
              key="card"
              {...cardMotion}
              className="pointer-events-auto absolute z-10 w-72 border-2 border-solid border-black bg-card p-5 shadow-md"
              onMouseEnter={open}
              onMouseLeave={close}
              onFocus={open}
              onBlur={close}
            >
              <PinCardBody pin={pin} publishedSlugs={publishedSlugs} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <section
      id="studies-map"
      className="bg-background px-4 pt-20 pb-4 md:px-8 md:pt-28 md:pb-6"
    >
      {/* Header — outlined eyebrow chip + Archivo-Black heading + muted subhead. */}
      <motion.div {...headerAnim} className="mx-auto max-w-3xl text-center">
        <span className="mb-4 inline-block border-2 border-solid border-black bg-secondary px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white">
          Global footprint
        </span>
        <h2 className="font-head text-3xl uppercase leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          <span className="text-secondary">Where</span> studies run
        </h2>
        <p className="mx-auto mt-4 max-w-2xl font-sans text-base text-muted-foreground sm:text-lg">
          From California labs to field sites abroad — every study, one platform.
        </p>
      </motion.div>

      {/* MAP — open/airy: NO frame (no border, no fill, no shadow). The container
          keeps only its sizing role (relative · 2:1 aspect · centered max-width)
          and is overflow-visible so a hovered card can spill past the map edges
          without being clipped. */}
      <div className="relative mx-auto mt-12 aspect-[1000/500] w-full max-w-[1280px] overflow-visible">
        {/* SVG layer: dotted continents + connectors + markers, all scale together.
            Purely visual — the interactive hit-areas/cards live in the overlay on
            top of this. The SVG fills the 2:1 box edge-to-edge (preserveAspectRatio
            meet on a matching ratio → no letterboxing), so viewBox coords map 1:1
            to container percentages, which the overlay relies on. */}
        <svg
          viewBox={`0 0 ${MAP_W} ${MAP_H}`}
          preserveAspectRatio="xMidYMid meet"
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          role="img"
          aria-label="World map showing where StudySync studies run: Stanford University and UC Irvine in California, the University of Calgary in Canada, plus a field site in Indonesia."
        >
          {/* STATIC dotted continents — the whole group fades in once. NEVER
              animate individual dots (≈3895 of them). */}
          <motion.g {...dotsAnim} opacity={reduceMotion ? 1 : undefined}>
            {WORLD_DOTS.map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r={1.3} fill="#8C1515" opacity={0.65} />
            ))}
          </motion.g>

          {/* Connector for the ACTIVE pin only — a visible dashed callout line
              from the dot across the gap to its inward-pulled card. Desktop-only
              (mobile cards stack below, no connectors). */}
          <g className="hidden md:block">
            <AnimatePresence>
              {activePin !== null && PINS[activePin] && (
                <Connector key={activePin} to={PINS[activePin]} />
              )}
            </AnimatePresence>
          </g>

          {/* Markers (pulsing) — always visible. Staggered ping phases so they
              don't beat in sync. */}
          {PINS.map((pin, i) => (
            <Marker key={pin.label} pin={pin} pingDelay={(i * 0.8) % 2.4} />
          ))}
        </svg>

        {/* DESKTOP hover overlay — one hover GROUP per pin (focusable hit-area +
            its card). Hidden on mobile. By default only the markers (in the SVG)
            show; a card reveals on hover/focus of its hit-area. The wrapper is
            pointer-events-none so only the hit-areas/cards inside are interactive
            and the rest of the map stays click-through. */}
        <div className="pointer-events-none absolute inset-0 hidden overflow-visible md:block">
          {PINS.map((pin, i) => (
            <PinHotspot key={pin.label} pin={pin} index={i} />
          ))}
        </div>
      </div>

      {/* MOBILE fallback — the absolute card layout is too cramped on small
          screens, so the pins render as a plain stacked list UNDER the map.
          Hidden from md up (where the floating cards take over). */}
      <div className="mx-auto mt-6 grid max-w-[1100px] gap-4 md:hidden">
        {PINS.map((pin, i) => (
          <motion.div
            key={pin.label}
            {...cardAnim(0.1 * i)}
            className="border-2 border-solid border-black bg-card p-4 shadow-md"
          >
            <PinCardBody pin={pin} publishedSlugs={publishedSlugs} />
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default StudiesMapSection;
